import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  // Thunks
  fetchUserSettings,
  updateUserSettings,
  updateAppearanceSettings,
  updateNotificationSettings,
  updatePrivacySettings,
  updateDisplaySettings,
  
  // Actions
  resetSettingsError,
  setTheme,
  setFontSize,
  toggleReducedMotion,
  toggleHighContrast,
  setLanguage,
  setMeasurementSystem,
  setDefaultTrainingView,
  setDefaultFeedSort,
  toggleNotifications,
  togglePushNotifications,
  toggleEmailNotifications,
  setQuietHours,
  setPrivacySetting,
  toggleAllowTagging,
  toggleShowOnlineStatus,
  addPreferredContent,
  removePreferredContent,
  addHiddenTopic,
  removeHiddenTopic,
  toggleSaveDataMode,
  toggleSyncEnabled,
  setSyncFrequency,
  toggleOfflineMode,
  setLastSyncTime,
  setSessionTimeout,
  toggleRememberMe,
  resetToDefaults,
  
  // Selectors
  selectAppearance,
  selectNotifications,
  selectPrivacy,
  selectDisplay,
  selectFeedPreferences,
  selectSync,
  selectAccount,
  selectSession,
  selectTheme,
  selectLanguage,
  selectMeasurementSystem,
  selectQuietHours,
  selectOfflineMode,
  selectPrivacyVisibility,
  selectDataSharing,
  selectSettingsLoading,
  selectSettingsError,
  selectLastUpdated
} from '../store/slices/settingsSlice';

import {
  ThemePreference,
  LanguageCode,
  MeasurementSystem,
  TrainingViewMode,
  FeedSortMode,
  PrivacySetting,
  AppearanceSettings,
  NotificationSettings,
  PrivacySettings,
  DisplaySettings,
  FeedPreferences,
  SyncSettings,
  AccountSettings,
  SessionSettings
} from '../types/settings';

/**
 * Custom hook for accessing and manipulating user settings
 */
export const useSettings = () => {
  const dispatch = useAppDispatch();
  
  // Select state from Redux store
  const appearance = useAppSelector(selectAppearance);
  const notifications = useAppSelector(selectNotifications);
  const privacy = useAppSelector(selectPrivacy);
  const display = useAppSelector(selectDisplay);
  const feed = useAppSelector(selectFeedPreferences);
  const sync = useAppSelector(selectSync);
  const account = useAppSelector(selectAccount);
  const session = useAppSelector(selectSession);
  
  // Common selectors
  const theme = useAppSelector(selectTheme);
  const language = useAppSelector(selectLanguage);
  const measurementSystem = useAppSelector(selectMeasurementSystem);
  const quietHours = useAppSelector(selectQuietHours);
  const offlineMode = useAppSelector(selectOfflineMode);
  const privacyVisibility = useAppSelector(selectPrivacyVisibility);
  const dataSharing = useAppSelector(selectDataSharing);
  
  // Status selectors
  const isLoading = useAppSelector(selectSettingsLoading);
  const error = useAppSelector(selectSettingsError);
  const lastUpdated = useAppSelector(selectLastUpdated);
  
  // Async thunks
  const loadSettings = useCallback((userId: string) => {
    return dispatch(fetchUserSettings(userId)).unwrap();
  }, [dispatch]);
  
  // Type for deeply partial settings, matching the slice
  type DeepPartialSettings = {
    appearance?: Partial<AppearanceSettings>;
    notifications?: Partial<NotificationSettings>;
    privacy?: Partial<PrivacySettings>;
    display?: Partial<DisplaySettings>;
    feed?: Partial<FeedPreferences>;
    sync?: Partial<SyncSettings>;
    account?: Partial<AccountSettings>;
    session?: Partial<SessionSettings>;
  };

  const saveSettings = useCallback((userId: string, settingsData: DeepPartialSettings) => {
    return dispatch(updateUserSettings({ userId, settings: settingsData })).unwrap();
  }, [dispatch]);
  
  // Section-specific update functions
  const updateAppearance = useCallback((userId: string, appearanceData: Partial<AppearanceSettings>) => {
    return dispatch(updateAppearanceSettings({ userId, appearance: appearanceData })).unwrap();
  }, [dispatch]);
  
  const updateNotifications = useCallback((userId: string, notificationsData: Partial<NotificationSettings>) => {
    return dispatch(updateNotificationSettings({ userId, notifications: notificationsData })).unwrap();
  }, [dispatch]);
  
  const updatePrivacy = useCallback((userId: string, privacyData: Partial<PrivacySettings>) => {
    return dispatch(updatePrivacySettings({ userId, privacy: privacyData })).unwrap();
  }, [dispatch]);
  
  const updateDisplay = useCallback((userId: string, displayData: Partial<DisplaySettings>) => {
    return dispatch(updateDisplaySettings({ userId, display: displayData })).unwrap();
  }, [dispatch]);
  
  // Individual settings actions
  
  // Appearance actions
  const changeTheme = useCallback((newTheme: ThemePreference) => {
    dispatch(setTheme(newTheme));
  }, [dispatch]);
  
  const changeFontSize = useCallback((size: 'small' | 'medium' | 'large') => {
    dispatch(setFontSize(size));
  }, [dispatch]);
  
  const toggleMotionReduction = useCallback(() => {
    dispatch(toggleReducedMotion());
  }, [dispatch]);
  
  const toggleContrastMode = useCallback(() => {
    dispatch(toggleHighContrast());
  }, [dispatch]);
  
  // Display actions
  const changeLanguage = useCallback((lang: LanguageCode) => {
    dispatch(setLanguage(lang));
  }, [dispatch]);
  
  const changeMeasurementSystem = useCallback((system: MeasurementSystem) => {
    dispatch(setMeasurementSystem(system));
  }, [dispatch]);
  
  const changeDefaultTrainingView = useCallback((view: TrainingViewMode) => {
    dispatch(setDefaultTrainingView(view));
  }, [dispatch]);
  
  const changeDefaultFeedSort = useCallback((sort: FeedSortMode) => {
    dispatch(setDefaultFeedSort(sort));
  }, [dispatch]);
  
  // Notification actions
  const enableNotifications = useCallback((enabled: boolean) => {
    dispatch(toggleNotifications(enabled));
  }, [dispatch]);
  
  const enablePushNotifications = useCallback((enabled: boolean) => {
    dispatch(togglePushNotifications(enabled));
  }, [dispatch]);
  
  const enableEmailNotifications = useCallback((enabled: boolean) => {
    dispatch(toggleEmailNotifications(enabled));
  }, [dispatch]);
  
  const configureQuietHours = useCallback((config: {enabled: boolean, start?: string, end?: string}) => {
    dispatch(setQuietHours(config));
  }, [dispatch]);
  
  // Privacy actions
  const changePrivacyVisibility = useCallback((
    setting: keyof typeof privacyVisibility, 
    visibility: PrivacySetting
  ) => {
    dispatch(setPrivacySetting({
      setting: setting as any,
      value: visibility
    }));
  }, [dispatch]);
  
  const allowTagging = useCallback((allowed: boolean) => {
    dispatch(toggleAllowTagging(allowed));
  }, [dispatch]);
  
  const showOnlineStatus = useCallback((visible: boolean) => {
    dispatch(toggleShowOnlineStatus(visible));
  }, [dispatch]);
  
  // Feed actions
  const addContentPreference = useCallback((contentType: string) => {
    dispatch(addPreferredContent(contentType));
  }, [dispatch]);
  
  const removeContentPreference = useCallback((contentType: string) => {
    dispatch(removePreferredContent(contentType));
  }, [dispatch]);
  
  const hideContent = useCallback((topic: string) => {
    dispatch(addHiddenTopic(topic));
  }, [dispatch]);
  
  const unhideContent = useCallback((topic: string) => {
    dispatch(removeHiddenTopic(topic));
  }, [dispatch]);
  
  const enableSaveDataMode = useCallback((enabled: boolean) => {
    dispatch(toggleSaveDataMode(enabled));
  }, [dispatch]);
  
  // Sync actions
  const enableSync = useCallback((enabled: boolean) => {
    dispatch(toggleSyncEnabled(enabled));
  }, [dispatch]);
  
  const changeSyncFrequency = useCallback((frequency: 'realtime' | 'hourly' | 'daily' | 'manual') => {
    dispatch(setSyncFrequency(frequency));
  }, [dispatch]);
  
  const enableOfflineMode = useCallback((enabled: boolean) => {
    dispatch(toggleOfflineMode(enabled));
  }, [dispatch]);
  
  const updateLastSyncTime = useCallback((timestamp: number) => {
    dispatch(setLastSyncTime(timestamp));
  }, [dispatch]);
  
  // Session actions
  const changeSessionTimeout = useCallback((minutes: number) => {
    dispatch(setSessionTimeout(minutes));
  }, [dispatch]);
  
  const enableRememberMe = useCallback((enabled: boolean) => {
    dispatch(toggleRememberMe(enabled));
  }, [dispatch]);
  
  // Reset settings
  const restoreDefaultSettings = useCallback(() => {
    dispatch(resetToDefaults());
  }, [dispatch]);
  
  // Clear error
  const clearError = useCallback(() => {
    dispatch(resetSettingsError());
  }, [dispatch]);
  
  return {
    // State
    appearance,
    notifications,
    privacy,
    display,
    feed,
    sync,
    account,
    session,
    
    // Common selectors
    theme,
    language,
    measurementSystem,
    quietHours,
    offlineMode,
    privacyVisibility,
    dataSharing,
    
    // Status
    isLoading,
    error,
    lastUpdated,
    
    // Thunks (API calls)
    loadSettings,
    saveSettings,
    updateAppearance,
    updateNotifications,
    updatePrivacy,
    updateDisplay,
    
    // Appearance actions
    changeTheme,
    changeFontSize,
    toggleMotionReduction,
    toggleContrastMode,
    
    // Display actions
    changeLanguage,
    changeMeasurementSystem,
    changeDefaultTrainingView,
    changeDefaultFeedSort,
    
    // Notification actions
    enableNotifications,
    enablePushNotifications,
    enableEmailNotifications,
    configureQuietHours,
    
    // Privacy actions
    changePrivacyVisibility,
    allowTagging,
    showOnlineStatus,
    
    // Feed actions
    addContentPreference,
    removeContentPreference,
    hideContent,
    unhideContent,
    enableSaveDataMode,
    
    // Sync actions
    enableSync,
    changeSyncFrequency,
    enableOfflineMode,
    updateLastSyncTime,
    
    // Session actions
    changeSessionTimeout,
    enableRememberMe,
    
    // Reset
    restoreDefaultSettings,
    clearError
  };
};

export default useSettings;